/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Card,
  DefaultMantineColor,
  Flex,
  Paper,
  Space,
  StyleProp,
  Text,
} from "@mantine/core"
import Dagre from "@dagrejs/dagre"
import ReactFlow, {
  useNodesState,
  useEdgesState,
  Handle,
  Position,
  useReactFlow,
  BaseEdge,
  getSimpleBezierPath,
  EdgeProps,
  EdgeLabelRenderer,
} from "reactflow"
import "reactflow/dist/style.css"
import {
  AbsentApprovalNodeType,
  ApprovalConfigNodeProps,
  ApprovalNodeProps,
  toFlowEdge,
} from "../../routes/app/admin-settings/absences/-components/shared"
import { UserName } from "../../blocks/persons-and-users/User"
import { AbsentApprovalGraph, AbsentApprovalNode } from "../../@client/Absents"
import { FSharpOption } from "../../@client/Microsoft_FSharp_Core"
import { AbsentApprovalStatusIcon } from "./absent-approval-status-icon"
import { useTranslation } from "react-i18next"

const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({ label: "" }))

const nodeWidth = 230

const nodeHeight = 100

const getLayoutedElements = (nodes: any, edges: any, options: any) => {
  g.setGraph({ rankdir: options.direction })

  edges.forEach((edge: any) => g.setEdge(edge.source, edge.target))
  nodes.forEach((node: any) =>
    g.setNode(node.id, { width: nodeWidth, height: nodeHeight })
  )

  Dagre.layout(g)

  return {
    nodes: nodes.map((node: any) => {
      const { x, y } = g.node(node.id)

      return { ...node, position: { x, y } }
    }),
    edges,
  }
}

function toFlowNode(activeNode: FSharpOption<AbsentApprovalNode>) {
  return (node: AbsentApprovalNode) => {
    return {
      id: node.nodeId,
      data: { ...node, isActive: activeNode?.nodeId === node.nodeId },
      position: { x: 0, y: 0 },
      type: "approval" as const,
    } satisfies AbsentApprovalNodeType
  }
}

export function FlowReview({
  data,
  activeNode,
}: {
  data: AbsentApprovalGraph
  activeNode: FSharpOption<AbsentApprovalNode>
}) {
  const initialNodes = React.useMemo(
    () => (data.nodes || []).map(toFlowNode(activeNode)),
    []
  )
  const initialEdges = React.useMemo(() => data.edges.map(toFlowEdge), [])
  const [nodes, setNodes, _] = useNodesState(initialNodes)
  const [edges, setEdges] = useEdgesState(initialEdges)
  const { fitView } = useReactFlow()

  React.useEffect(() => {
    const layouted = getLayoutedElements(nodes, edges, { direction: "LR" })
    setNodes([...layouted.nodes])
    setEdges([...layouted.edges])
    window.requestAnimationFrame(() => {
      fitView()
    })
  }, [])

  const nodeTypes = React.useMemo(
    () => ({
      trigger: TriggerNode,
      approval: ApprovalNode,
    }),
    []
  )

  const edgeTypes = React.useMemo(() => ({ custom: CustomEdge }), [])

  return (
    <div style={{ width: "100%", height: 400 }}>
      <ReactFlow
        proOptions={{ hideAttribution: true }}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        nodes={nodes}
        edges={edges}
        snapToGrid
      />
    </div>
  )
}

function CustomEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getSimpleBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  return (
    <>
      <BaseEdge path={edgePath} markerEnd={markerEnd} style={style} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            // everything inside EdgeLabelRenderer has no pointer events by default
            // if you have an interactive element, set pointer-events: all
            pointerEvents: "all",
          }}
        ></div>
      </EdgeLabelRenderer>
    </>
  )
}

function TriggerNode({}: ApprovalConfigNodeProps) {
  const { t } = useTranslation("app_server")
  return (
    <>
      <Paper
        bg="orange"
        c="white"
        radius="xl"
        withBorder
        px="md"
        shadow="xl"
        style={{ alignContent: "center", justifyItems: "center" }}
      >
        <Text>{t("RequestCreated")}</Text>
      </Paper>
      <Handle type="source" position={Position.Right} />
    </>
  )
}

function ApprovalNode(props: ApprovalNodeProps) {
  const x = props.data.details
  if (x.Case === "Trigger") {
    return (
      <NodeSkelet title={"Trigger"} highlight={props.data.isActive} {...props}>
        {() => <div />}
      </NodeSkelet>
    )
  }

  if (x.Case === "ContractTypeOrContractTypeCategoryCondition") {
    return (
      <NodeSkelet
        title={"ContractTypeOrContractTypeCategoryCondition"}
        highlight={props.data.isActive}
        {...props}
      >
        {() => <div />}
      </NodeSkelet>
    )
  }
  if (x.Case === "DepartmentOrWorkareaCondition") {
    return (
      <NodeSkelet
        title={"DepartmentOrWorkareaCondition"}
        highlight={props.data.isActive}
        {...props}
      >
        {() => <div />}
      </NodeSkelet>
    )
  }
  if (x.Case === "NotifyUsers") {
    return (
      <NodeSkelet
        title={"Notify Users"}
        highlight={props.data.isActive}
        {...props}
      >
        {() => <div />}
      </NodeSkelet>
    )
  }
  const step = x.Fields
  return (
    <>
      <NodeSkelet
        icon={<AbsentApprovalStatusIcon size="sm" status={step.result} />}
        title="Genehmigung"
        highlight={props.data.isActive}
        {...props}
      >
        {() => (
          <>
            {step.approvals.map((approval, i) => (
              <Flex gap="xs" key={i}>
                <Text>
                  <UserName key={approval.userId} id={approval.userId} />
                </Text>
                {step.result.Case === "Pending" ? null : (
                  <AbsentApprovalStatusIcon
                    size="xs"
                    status={approval.result}
                  />
                )}
              </Flex>
            ))}
          </>
        )}
      </NodeSkelet>
    </>
  )
}

function NodeSkelet({
  children,
  title,
  titleColor,
  sourceHandle,
  targetHandle,
  icon,
  highlight,
  ...props
}: {
  icon?: React.ReactNode
  titleColor?: StyleProp<DefaultMantineColor>
  title: string
  children: (editing: boolean) => React.ReactNode | React.ReactNode[]
  sourceHandle?: boolean
  targetHandle?: boolean
  showAsActive?: boolean
  highlight?: boolean
} & ApprovalNodeProps) {
  return (
    <>
      {targetHandle === false ? null : (
        <Handle type="target" position={Position.Left} isConnectable={false} />
      )}
      <Card
        withBorder
        styles={(theme) =>
          props.selected
            ? { root: { borderColor: theme.colors.gray[3] } }
            : highlight
              ? {
                  root: {
                    borderColor: theme.colors.blue[3],
                    borderWidth: 2,
                  },
                }
              : {}
        }
        shadow="xs"
        w={nodeWidth}
      >
        <Card.Section p="md">
          <Flex gap={"xs"} align={"center"} justify={"space-between"}>
            <Flex align={"start"} gap="xs">
              {icon}
              <Text maw={250} c={titleColor} variant="text">
                {title}
              </Text>
            </Flex>
          </Flex>
          <Space h={"xs"} />
          {children(false)}
        </Card.Section>
      </Card>
      {sourceHandle === false ? null : (
        <Handle type="source" position={Position.Right} isConnectable={false} />
      )}
    </>
  )
}
